import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Gauge, Plus, Search, Filter, Download, AlertTriangle, CheckCircle2, 
  Clock, XCircle, Settings, ChevronDown, ChevronUp, Edit, Eye, 
  Calendar, User, Building2, FileText, History, Shield, Wrench, Archive,
  Home, ChevronRight, Loader2, Trash2, AlertOctagon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors for equipment
const STATUS_COLORS = {
  "In Service": "bg-green-600",
  "In Service - Approved": "bg-green-600",
  "Due Soon": "bg-amber-500",
  "Overdue": "bg-red-600",
  "Scheduled for Calibration": "bg-blue-500",
  "Out for Calibration": "bg-purple-500",
  "Calibration Review Pending": "bg-cyan-500",
  "Quarantined": "bg-red-800",
  "Damaged": "bg-red-900",
  "Retired": "bg-gray-600",
  "Archived": "bg-gray-500",
  "Scrapped": "bg-orange-800"
};

const STATUS_BORDER_COLORS = {
  "In Service": "border-green-600",
  "In Service - Approved": "border-green-600",
  "Due Soon": "border-amber-500",
  "Overdue": "border-red-600",
  "Scheduled for Calibration": "border-blue-500",
  "Out for Calibration": "border-purple-500",
  "Calibration Review Pending": "border-cyan-500",
  "Quarantined": "border-red-800",
  "Damaged": "border-red-900",
  "Retired": "border-gray-600",
  "Archived": "border-gray-500",
  "Scrapped": "border-orange-800"
};

// Calibration Dashboard Summary Component
export const CalibrationSummary = ({ onViewAll }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/equipment/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching calibration stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card/50 border border-border rounded-sm p-6">
        <p className="text-muted-foreground">Loading calibration data...</p>
      </div>
    );
  }

  if (!stats) return null;

  const summaryCards = [
    { label: 'Total Active', value: stats.total_active, color: 'bg-blue-600', icon: Gauge },
    { label: 'In Calibration', value: stats.in_calibration, color: 'bg-green-600', icon: CheckCircle2 },
    { label: 'Due Soon', value: stats.due_soon, color: 'bg-amber-500', icon: Clock },
    { label: 'Overdue', value: stats.overdue, color: 'bg-red-600', icon: AlertTriangle },
    { label: 'Quarantined', value: stats.quarantined, color: 'bg-red-800', icon: XCircle },
    { label: 'Out for Cal.', value: stats.out_for_calibration, color: 'bg-purple-500', icon: Settings }
  ];

  return (
    <div className="space-y-4" data-testid="calibration-summary">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-600/20 rounded-sm flex items-center justify-center">
            <Gauge className="w-5 h-5 text-cyan-500" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white uppercase tracking-tight">
              Equipment Calibration
            </h3>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              ISO9001:2015 Monitoring & Measuring Resources
            </p>
          </div>
        </div>
        <Button
          onClick={onViewAll}
          variant="outline"
          className="border-cyan-600 text-cyan-400 hover:bg-cyan-950/50 font-bold uppercase tracking-wide text-xs"
          data-testid="view-equipment-register-btn"
        >
          <Eye className="w-4 h-4 mr-2" />
          View Register
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          const isAlert = card.label === 'Due Soon' || card.label === 'Overdue' || card.label === 'Quarantined';
          return (
            <div
              key={card.label}
              className={`${card.color}/20 border ${card.color.replace('bg-', 'border-')} rounded-sm p-3 ${
                isAlert && card.value > 0 ? 'animate-pulse' : ''
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${card.color.replace('bg-', 'text-')}`} />
                <p className="text-xs text-muted-foreground uppercase tracking-wider truncate">
                  {card.label}
                </p>
              </div>
              <p className="text-2xl font-bold text-white">{card.value}</p>
            </div>
          );
        })}
      </div>

      {/* Alerts */}
      {(stats.overdue > 0 || stats.due_soon > 0 || stats.missing_certificate > 0) && (
        <div className="flex flex-wrap gap-2">
          {stats.overdue > 0 && (
            <div className="bg-red-950/50 border border-red-600 rounded-sm px-3 py-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-red-400 text-sm font-bold">{stats.overdue} OVERDUE</span>
            </div>
          )}
          {stats.due_soon > 0 && (
            <div className="bg-amber-950/50 border border-amber-600 rounded-sm px-3 py-1 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-amber-400 text-sm font-bold">{stats.due_soon} DUE SOON</span>
            </div>
          )}
          {stats.missing_certificate > 0 && (
            <div className="bg-orange-950/50 border border-orange-600 rounded-sm px-3 py-1 flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-500" />
              <span className="text-orange-400 text-sm font-bold">{stats.missing_certificate} MISSING CERT</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Calibration Module Component
const CalibrationModule = ({ onClose }) => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const handleBack = () => {
    if (onClose) onClose();
    else navigate('/');
  };
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [calibrationDialogOpen, setCalibrationDialogOpen] = useState(false);
  const [checkDialogOpen, setCheckDialogOpen] = useState(false);
  const [retireDialogOpen, setRetireDialogOpen] = useState(false);
  const [retireReason, setRetireReason] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [scrapDialogOpen, setScrapDialogOpen] = useState(false);
  const [scrapData, setScrapData] = useState({
    scrap_date: new Date().toISOString().split('T')[0],
    scrap_reason: '',
    scrap_category: '',
    notes: '',
    scrapped_by: '',
    approved_by: ''
  });
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [viewFilter, setViewFilter] = useState('active');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Form states
  const [newEquipment, setNewEquipment] = useState({
    equipment_type: '',
    description: '',
    serial_number: '',
    asset_number: '',
    manufacturer: '',
    model: '',
    measuring_range: '',
    resolution: '',
    accuracy_class: '',
    tolerance: '',
    unit_of_measure: '',
    category: '',
    criticality: 'Standard',
    calibration_method: 'External',
    department: '',
    storage_location: '',
    custodian: '',
    responsible_person: '',
    calibration_frequency: 'annually',
    calibration_supplier: '',
    traceability_standard: '',
    reminder_days: 30,
    notes: ''
  });
  
  const [calibrationData, setCalibrationData] = useState({
    calibration_date: '',
    calibrated_by: '',
    calibration_method: 'External',
    calibration_supplier: '',
    certificate_number: '',
    reference_standard: '',
    result: 'Pass',
    as_found_condition: '',
    as_left_condition: '',
    measurement_deviations: '',
    adjustments_performed: '',
    notes: '',
    temperature: '',
    humidity: ''
  });
  
  const [checkData, setCheckData] = useState({
    check_date: '',
    checked_by: '',
    check_type: '',
    result: 'Pass',
    notes: '',
    master_reference_used: ''
  });

  useEffect(() => {
    fetchData();
  }, [viewFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applyFilters();
  }, [equipment, searchQuery, statusFilter, categoryFilter, departmentFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const includeInactive = viewFilter === 'all' || viewFilter === 'scrapped' || viewFilter === 'retired' || viewFilter === 'archived';
      const statusParam = viewFilter === 'scrapped' ? '&status=Scrapped' : viewFilter === 'retired' ? '&status=Retired' : viewFilter === 'archived' ? '&status=Archived' : '';
      const [equipmentRes, configRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/equipment?include_inactive=${includeInactive}${statusParam}`),
        axios.get(`${BACKEND_URL}/api/calibration/config`),
        axios.get(`${BACKEND_URL}/api/equipment/stats`)
      ]);
      setEquipment(equipmentRes.data);
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load equipment data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...equipment];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(eq => 
        eq.equipment_id?.toLowerCase().includes(query) ||
        eq.serial_number?.toLowerCase().includes(query) ||
        eq.description?.toLowerCase().includes(query) ||
        eq.custodian?.toLowerCase().includes(query) ||
        eq.manufacturer?.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter) {
      if (statusFilter === 'Overdue') {
        filtered = filtered.filter(eq => eq.is_overdue);
      } else if (statusFilter === 'Due Soon') {
        filtered = filtered.filter(eq => eq.is_due_soon);
      } else {
        filtered = filtered.filter(eq => eq.status === statusFilter);
      }
    }
    
    if (categoryFilter) {
      filtered = filtered.filter(eq => eq.category === categoryFilter);
    }
    
    if (departmentFilter) {
      filtered = filtered.filter(eq => eq.department === departmentFilter);
    }
    
    setFilteredEquipment(filtered);
  };

  const handleCreateEquipment = async (e) => {
    e.preventDefault();
    
    if (!newEquipment.equipment_type || !newEquipment.serial_number || !newEquipment.category || !newEquipment.description) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/equipment`, {
        ...newEquipment,
        created_by: 'User'
      });
      toast.success(`Equipment ${response.data.equipment_id} created`);
      setCreateDialogOpen(false);
      resetNewEquipmentForm();
      fetchData();
    } catch (error) {
      console.error('Error creating equipment:', error);
      toast.error('Failed to create equipment');
    }
  };

  const resetNewEquipmentForm = () => {
    setNewEquipment({
      equipment_type: '',
      description: '',
      serial_number: '',
      asset_number: '',
      manufacturer: '',
      model: '',
      measuring_range: '',
      resolution: '',
      accuracy_class: '',
      tolerance: '',
      unit_of_measure: '',
      category: '',
      criticality: 'Standard',
      calibration_method: 'External',
      department: '',
      storage_location: '',
      custodian: '',
      responsible_person: '',
      calibration_frequency: 'annually',
      calibration_supplier: '',
      traceability_standard: '',
      reminder_days: 30,
      notes: ''
    });
  };

  const handleEquipmentClick = async (eq) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/equipment/${eq.equipment_id}`);
      setSelectedEquipment(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error fetching equipment details:', error);
      toast.error('Failed to load equipment details');
    }
  };

  const handleRecordCalibration = async (e) => {
    e.preventDefault();
    
    if (!calibrationData.calibration_date || !calibrationData.calibrated_by) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}/calibration?recorded_by=User`,
        calibrationData
      );
      toast.success('Calibration recorded successfully');
      setCalibrationDialogOpen(false);
      resetCalibrationForm();
      
      // Refresh equipment details
      const response = await axios.get(`${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}`);
      setSelectedEquipment(response.data);
      fetchData();
    } catch (error) {
      console.error('Error recording calibration:', error);
      toast.error('Failed to record calibration');
    }
  };

  const resetCalibrationForm = () => {
    setCalibrationData({
      calibration_date: '',
      calibrated_by: '',
      calibration_method: 'External',
      calibration_supplier: '',
      certificate_number: '',
      reference_standard: '',
      result: 'Pass',
      as_found_condition: '',
      as_left_condition: '',
      measurement_deviations: '',
      adjustments_performed: '',
      notes: '',
      temperature: '',
      humidity: ''
    });
  };

  const handleRecordCheck = async (e) => {
    e.preventDefault();
    
    if (!checkData.check_date || !checkData.checked_by || !checkData.check_type) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}/intermediate-check?recorded_by=User`,
        checkData
      );
      
      if (response.data.equipment_quarantined) {
        toast.error('Equipment quarantined due to failed check');
      } else {
        toast.success('Check recorded successfully');
      }
      
      setCheckDialogOpen(false);
      resetCheckForm();
      
      // Refresh equipment details
      const eqResponse = await axios.get(`${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}`);
      setSelectedEquipment(eqResponse.data);
      fetchData();
    } catch (error) {
      console.error('Error recording check:', error);
      toast.error('Failed to record check');
    }
  };

  const resetCheckForm = () => {
    setCheckData({
      check_date: '',
      checked_by: '',
      check_type: '',
      result: 'Pass',
      notes: '',
      master_reference_used: ''
    });
  };

  const handleStatusChange = async (newStatus, reason = '') => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}/status?status=${newStatus}&reason=${reason}&updated_by=User`
      );
      toast.success(`Status updated to ${newStatus}`);
      
      // Refresh
      const response = await axios.get(`${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}`);
      setSelectedEquipment(response.data);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleRetireEquipment = async () => {
    if (!selectedEquipment) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}/retire?reason=${encodeURIComponent(retireReason || 'Equipment retired')}&retired_by=${encodeURIComponent(user?.email || 'User')}`
      );
      toast.success(`${selectedEquipment.equipment_id} has been retired`);
      setRetireDialogOpen(false);
      setRetireReason('');
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error retiring equipment:', error);
      toast.error('Failed to retire equipment');
    }
  };

  const handleArchiveEquipment = async () => {
    if (!selectedEquipment) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}/archive?reason=${encodeURIComponent(retireReason || 'Equipment archived')}&archived_by=${encodeURIComponent(user?.email || 'User')}`
      );
      toast.success(`${selectedEquipment.equipment_id} has been archived`);
      setRetireDialogOpen(false);
      setRetireReason('');
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to archive equipment');
    }
  };

  const handleDeleteEquipment = async () => {
    if (!selectedEquipment || !deleteReason) return;
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}?admin_email=${encodeURIComponent(user?.email || '')}&reason=${encodeURIComponent(deleteReason)}`
      );
      toast.success(`${selectedEquipment.equipment_id} permanently deleted`);
      setDeleteDialogOpen(false);
      setDeleteReason('');
      setDetailDialogOpen(false);
      setSelectedEquipment(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to delete';
      if (error.response?.status === 409) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally { setDeleteLoading(false); }
  };

  const openDeleteDialog = async () => {
    if (!selectedEquipment) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}/delete-check`);
      setDeleteCheck(res.data);
      setDeleteReason('');
      setDeleteDialogOpen(true);
    } catch { toast.error('Failed to check equipment'); }
  };

  const handleScrapEquipment = async () => {
    if (!selectedEquipment) return;
    if (!scrapData.scrap_reason || !scrapData.scrap_category || !scrapData.scrap_date) {
      toast.error('Scrap date, reason, and category are required');
      return;
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}/scrap`,
        {
          ...scrapData,
          scrapped_by: scrapData.scrapped_by || user?.email || 'User'
        }
      );
      toast.success(`${selectedEquipment.equipment_id} has been scrapped`);
      setScrapDialogOpen(false);
      setScrapData({ scrap_date: new Date().toISOString().split('T')[0], scrap_reason: '', scrap_category: '', notes: '', scrapped_by: '', approved_by: '' });
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error scrapping equipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to scrap equipment');
    }
  };

  const openEditDialog = () => {
    if (!selectedEquipment) return;
    setEditData({
      equipment_type: selectedEquipment.equipment_type || '',
      description: selectedEquipment.description || '',
      serial_number: selectedEquipment.serial_number || '',
      asset_number: selectedEquipment.asset_number || '',
      manufacturer: selectedEquipment.manufacturer || '',
      model: selectedEquipment.model || '',
      measuring_range: selectedEquipment.measuring_range || '',
      resolution: selectedEquipment.resolution || '',
      accuracy_class: selectedEquipment.accuracy_class || '',
      tolerance: selectedEquipment.tolerance || '',
      unit_of_measure: selectedEquipment.unit_of_measure || '',
      category: selectedEquipment.category || '',
      criticality: selectedEquipment.criticality || 'Standard',
      calibration_method: selectedEquipment.calibration_method || 'External',
      department: selectedEquipment.department || '',
      storage_location: selectedEquipment.storage_location || '',
      custodian: selectedEquipment.custodian || '',
      responsible_person: selectedEquipment.responsible_person || '',
      calibration_frequency: selectedEquipment.calibration_frequency || 'annually',
      calibration_supplier: selectedEquipment.calibration_supplier || '',
      traceability_standard: selectedEquipment.traceability_standard || '',
      reminder_days: selectedEquipment.reminder_days || 30,
      notes: selectedEquipment.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedEquipment) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}?updated_by=${encodeURIComponent(user?.email || 'User')}`,
        editData
      );
      toast.success(`${selectedEquipment.equipment_id} updated`);
      setEditDialogOpen(false);
      // Refresh detail and list
      const res = await axios.get(`${BACKEND_URL}/api/equipment/${selectedEquipment.equipment_id}`);
      setSelectedEquipment(res.data);
      fetchData();
    } catch (error) {
      console.error('Error updating equipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to update equipment');
    }
  };

  const handleExportRegister = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/equipment/report/master-register`);
      const data = response.data;
      
      // Prepare data for Excel
      const exportData = data.map(eq => ({
        'Equipment ID': eq.equipment_id,
        'Type': eq.equipment_type,
        'Description': eq.description,
        'Serial Number': eq.serial_number,
        'Asset Number': eq.asset_number,
        'Category': eq.category,
        'Manufacturer': eq.manufacturer,
        'Model': eq.model,
        'Range': eq.measuring_range,
        'Resolution': eq.resolution,
        'Tolerance': eq.tolerance,
        'Status': eq.calculated_status || eq.status,
        'Department': eq.department,
        'Custodian': eq.custodian,
        'Calibration Frequency': eq.calibration_frequency,
        'Last Calibration': eq.last_calibration_date || 'N/A',
        'Next Due Date': eq.next_due_date || 'N/A',
        'Days Until Due': eq.days_until_due ?? 'N/A',
        'Certificate Number': eq.current_certificate_number || 'N/A',
        'Calibration Supplier': eq.calibration_supplier || 'N/A',
        'Criticality': eq.criticality,
        'Notes': eq.notes || ''
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Equipment Register');
      
      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({ wch: Math.max(key.length, 15) }));
      ws['!cols'] = colWidths;
      
      XLSX.writeFile(wb, `Equipment_Calibration_Register_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Register exported successfully');
    } catch (error) {
      console.error('Error exporting register:', error);
      toast.error('Failed to export register');
    }
  };

  const getStatusDisplay = (eq) => {
    if (eq.calculated_status) return eq.calculated_status;
    if (eq.is_overdue) return 'Overdue';
    if (eq.is_due_soon) return 'Due Soon';
    return eq.status || 'In Service';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white" data-testid="calibration-module">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-cal" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(20, 184, 166, 0.15)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(20, 184, 166, 0.15)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-cal)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(20, 184, 166, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-teal-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} className="text-slate-400 hover:text-teal-400 hover:bg-teal-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide">Equipment Calibration</h1>
                <p className="text-xs text-slate-500">ISO9001:2015 Monitoring & Measuring Resources</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-teal-400 font-medium">Calibration</span>
          </nav>
          <div className="flex items-center gap-3">
            <Button onClick={handleExportRegister} variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10" data-testid="export-register-btn">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 border border-teal-500/50 hover:shadow-[0_0_20px_rgba(20,184,166,0.3)]" data-testid="add-equipment-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Equipment
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1600px] mx-auto space-y-6">

      {/* Summary Stats */}
      {stats && (
        <div className="bg-slate-900/60 backdrop-blur-sm border border-teal-500/10 rounded-lg px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Active</p>
              <p className="text-2xl font-bold font-mono text-white">{stats.total_active}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">In Calibration</p>
              <p className="text-2xl font-bold font-mono text-green-400">{stats.in_calibration}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Due Soon</p>
              <p className={`text-2xl font-bold font-mono text-amber-400 ${stats.due_soon > 0 ? 'animate-pulse' : ''}`}>{stats.due_soon}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Overdue</p>
              <p className={`text-2xl font-bold font-mono text-red-400 ${stats.overdue > 0 ? 'animate-pulse' : ''}`}>{stats.overdue}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Quarantined</p>
              <p className="text-2xl font-bold font-mono text-red-500">{stats.quarantined}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Out for Cal.</p>
              <p className="text-2xl font-bold font-mono text-purple-400">{stats.out_for_calibration}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Scrapped</p>
              <p className="text-2xl font-bold font-mono text-orange-400">{stats.scrapped || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* View Filter Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'active', label: 'Active', color: 'teal' },
          { key: 'scrapped', label: 'Scrapped', color: 'orange' },
          { key: 'retired', label: 'Retired', color: 'gray' },
          { key: 'archived', label: 'Archived', color: 'gray' },
          { key: 'all', label: 'All', color: 'slate' }
        ].map(f => (
          <Button
            key={f.key}
            size="sm"
            onClick={() => { setViewFilter(f.key); setStatusFilter(''); }}
            className={`font-bold uppercase tracking-wide text-xs ${
              viewFilter === f.key
                ? f.color === 'teal' ? 'bg-teal-500/20 text-teal-400 border-teal-500/50'
                  : f.color === 'orange' ? 'bg-orange-500/20 text-orange-400 border-orange-500/50'
                  : f.color === 'gray' ? 'bg-gray-500/20 text-gray-400 border-gray-500/50'
                  : 'bg-slate-500/20 text-slate-300 border-slate-500/50'
                : 'bg-slate-900/40 text-slate-500 border-slate-800 hover:border-slate-600'
            } border`}
            data-testid={`view-filter-${f.key}`}
          >
            {f.label} {f.key === 'scrapped' && stats?.scrapped ? `(${stats.scrapped})` : ''}
          </Button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="bg-slate-900/40 backdrop-blur-sm border border-teal-500/10 rounded-lg px-4 py-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search equipment..."
              className="pl-10 bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-teal-500/50"
              data-testid="equipment-search-input"
            />
          </div>
          
          <Select value={statusFilter || 'all'} onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}>
            <SelectTrigger className="w-[150px] bg-slate-950/50 border-slate-800">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Overdue">Overdue</SelectItem>
              <SelectItem value="Due Soon">Due Soon</SelectItem>
              {config?.statuses?.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={categoryFilter || 'all'} onValueChange={(val) => setCategoryFilter(val === 'all' ? '' : val)}>
            <SelectTrigger className="w-[150px] bg-slate-950/50 border-slate-800">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {config?.categories?.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={departmentFilter || 'all'} onValueChange={(val) => setDepartmentFilter(val === 'all' ? '' : val)}>
            <SelectTrigger className="w-[150px] bg-slate-950/50 border-slate-800">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {config?.departments?.map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={() => { setStatusFilter(''); setCategoryFilter(''); setDepartmentFilter(''); setSearchQuery(''); }}
            className="border-slate-700 hover:border-teal-500/50"
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Equipment List */}
      <div className="bg-slate-900/40 backdrop-blur-md border border-teal-500/10 rounded-xl overflow-hidden">
        <div className="bg-slate-900/60 border-b border-slate-800 px-4 py-3">
          <p className="text-sm text-slate-400">
            Showing {filteredEquipment.length} of {equipment.length} instruments
          </p>
        </div>
        
        <div className="divide-y divide-slate-800/50 max-h-[600px] overflow-y-auto">
          {filteredEquipment.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-slate-900/60 border border-slate-800 flex items-center justify-center mx-auto mb-6">
                <Gauge className="w-10 h-10 text-slate-600" />
              </div>
              <p className="text-slate-400 mb-4">No equipment found</p>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 border border-teal-500/50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Equipment
              </Button>
            </div>
          ) : (
            filteredEquipment.map((eq) => {
              const displayStatus = getStatusDisplay(eq);
              const statusColor = STATUS_COLORS[displayStatus] || 'bg-gray-500';
              const borderColor = STATUS_BORDER_COLORS[displayStatus] || 'border-gray-500';
              
              return (
                <div
                  key={eq.equipment_id}
                  onClick={() => handleEquipmentClick(eq)}
                  className={`p-4 hover:bg-muted/20 cursor-pointer border-l-4 ${borderColor} transition-colors`}
                  data-testid={`equipment-row-${eq.equipment_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-white font-bold">{eq.equipment_id}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${statusColor} text-white`}>
                            {displayStatus}
                          </span>
                          {eq.is_blocked && (
                            <span className="text-xs px-2 py-0.5 rounded-sm font-bold bg-red-900 text-red-200">
                              BLOCKED
                            </span>
                          )}
                        </div>
                        <p className="text-white font-medium">{eq.description}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                          <span>S/N: {eq.serial_number}</span>
                          <span>{eq.category}</span>
                          {eq.department && <span>{eq.department}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground mb-1">
                        {eq.next_due_date ? (
                          <>
                            <span className="text-xs uppercase">Next Due: </span>
                            <span className={eq.is_overdue ? 'text-red-400 font-bold' : eq.is_due_soon ? 'text-amber-400 font-bold' : 'text-white'}>
                              {formatDate(eq.next_due_date)}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs">No calibration scheduled</span>
                        )}
                      </div>
                      {eq.days_until_due !== null && eq.days_until_due !== undefined && (
                        <p className={`text-xs font-bold ${
                          eq.days_until_due < 0 ? 'text-red-400' : 
                          eq.days_until_due <= 30 ? 'text-amber-400' : 'text-green-400'
                        }`}>
                          {eq.days_until_due < 0 
                            ? `${Math.abs(eq.days_until_due)} days overdue`
                            : `${eq.days_until_due} days remaining`
                          }
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

        </div>
      </main>

      {/* Create Equipment Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-slate-950 border-teal-500/30 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-equipment-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <Plus className="w-5 h-5 text-teal-400" />
              Add New Equipment
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Register new measurement equipment for calibration tracking.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateEquipment} className="space-y-6">
            {/* Identification */}
            <div className="border border-teal-800/50 rounded-lg p-4 bg-teal-950/20">
              <h4 className="text-teal-400 font-bold uppercase tracking-wide mb-3 text-sm">
                Identification
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Type *</Label>
                  <Input
                    value={newEquipment.equipment_type}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, equipment_type: e.target.value }))}
                    className="bg-slate-950/50 border-slate-800"
                    placeholder="e.g., Digital Caliper"
                    data-testid="eq-type-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Category *</Label>
                  <Select
                    value={newEquipment.category}
                    onValueChange={(val) => setNewEquipment(prev => ({ ...prev, category: val }))}
                  >
                    <SelectTrigger className="bg-slate-950/50 border-slate-800" data-testid="eq-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.categories?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Description *</Label>
                  <Input
                    value={newEquipment.description}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-slate-950/50 border-slate-800"
                    placeholder="Equipment description"
                    data-testid="eq-description-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Serial Number *</Label>
                  <Input
                    value={newEquipment.serial_number}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, serial_number: e.target.value }))}
                    className="bg-slate-950/50 border-slate-800"
                    placeholder="Serial number"
                    data-testid="eq-serial-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Asset Number</Label>
                  <Input
                    value={newEquipment.asset_number}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, asset_number: e.target.value }))}
                    className="bg-slate-950/50 border-slate-800"
                    placeholder="Asset tag number"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Manufacturer</Label>
                  <Input
                    value={newEquipment.manufacturer}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, manufacturer: e.target.value }))}
                    className="bg-slate-950/50 border-slate-800"
                    placeholder="Manufacturer name"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Model</Label>
                  <Input
                    value={newEquipment.model}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, model: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="Model number"
                  />
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="border border-blue-800 rounded-sm p-4 bg-blue-950/20">
              <h4 className="text-blue-400 font-bold uppercase tracking-wide mb-3 text-sm">
                Specifications
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Measuring Range</Label>
                  <Input
                    value={newEquipment.measuring_range}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, measuring_range: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="e.g., 0-150mm"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Resolution</Label>
                  <Input
                    value={newEquipment.resolution}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, resolution: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="e.g., 0.01mm"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Tolerance</Label>
                  <Input
                    value={newEquipment.tolerance}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, tolerance: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="e.g., ±0.02mm"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Accuracy Class</Label>
                  <Input
                    value={newEquipment.accuracy_class}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, accuracy_class: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="e.g., Grade 1"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Unit of Measure</Label>
                  <Input
                    value={newEquipment.unit_of_measure}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, unit_of_measure: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="e.g., mm, inch"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Criticality</Label>
                  <Select
                    value={newEquipment.criticality}
                    onValueChange={(val) => setNewEquipment(prev => ({ ...prev, criticality: val }))}
                  >
                    <SelectTrigger className="bg-muted/50 border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.criticality_levels?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Location & Ownership */}
            <div className="border border-purple-800 rounded-sm p-4 bg-purple-950/20">
              <h4 className="text-purple-400 font-bold uppercase tracking-wide mb-3 text-sm">
                Location & Ownership
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Department</Label>
                  <Select
                    value={newEquipment.department}
                    onValueChange={(val) => setNewEquipment(prev => ({ ...prev, department: val }))}
                  >
                    <SelectTrigger className="bg-muted/50 border-border">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.departments?.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Storage Location</Label>
                  <Input
                    value={newEquipment.storage_location}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, storage_location: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="e.g., QA Lab Cabinet A"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Custodian</Label>
                  <Input
                    value={newEquipment.custodian}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, custodian: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="Person responsible for equipment"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Responsible Person</Label>
                  <Input
                    value={newEquipment.responsible_person}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, responsible_person: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="Calibration responsible person"
                  />
                </div>
              </div>
            </div>

            {/* Calibration Setup */}
            <div className="border border-green-800 rounded-sm p-4 bg-green-950/20">
              <h4 className="text-green-400 font-bold uppercase tracking-wide mb-3 text-sm">
                Calibration Setup
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Calibration Frequency</Label>
                  <Select
                    value={newEquipment.calibration_frequency}
                    onValueChange={(val) => setNewEquipment(prev => ({ ...prev, calibration_frequency: val }))}
                  >
                    <SelectTrigger className="bg-muted/50 border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.frequencies?.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Calibration Method</Label>
                  <Select
                    value={newEquipment.calibration_method}
                    onValueChange={(val) => setNewEquipment(prev => ({ ...prev, calibration_method: val }))}
                  >
                    <SelectTrigger className="bg-muted/50 border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.calibration_methods?.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Reminder Days</Label>
                  <Input
                    type="number"
                    value={newEquipment.reminder_days}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, reminder_days: parseInt(e.target.value) || 30 }))}
                    className="bg-muted/50 border-border"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Calibration Supplier</Label>
                  <Input
                    value={newEquipment.calibration_supplier}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, calibration_supplier: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="External lab name"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-white uppercase tracking-wider text-xs">Traceability Standard</Label>
                  <Input
                    value={newEquipment.traceability_standard}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, traceability_standard: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="e.g., NIST, ISO 17025"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Notes</Label>
              <Textarea
                value={newEquipment.notes}
                onChange={(e) => setNewEquipment(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-muted/50 border-border min-h-[80px]"
                placeholder="Additional notes..."
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                className="border-border"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold uppercase tracking-wide"
                data-testid="submit-equipment-btn"
              >
                Add Equipment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Equipment Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="equipment-detail-dialog">
          {selectedEquipment && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-cyan-500" />
                    {selectedEquipment.equipment_id}
                  </DialogTitle>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-sm font-bold ${STATUS_COLORS[getStatusDisplay(selectedEquipment)]} text-white`}>
                      {getStatusDisplay(selectedEquipment)}
                    </span>
                    {selectedEquipment.is_blocked && (
                      <span className="text-xs px-2 py-1 rounded-sm font-bold bg-red-900 text-red-200">
                        BLOCKED
                      </span>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="bg-muted/30 border border-border">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="calibration">Calibration</TabsTrigger>
                  <TabsTrigger value="checks">Checks</TabsTrigger>
                  <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Type</p>
                      <p className="text-white font-bold">{selectedEquipment.equipment_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Category</p>
                      <p className="text-white font-bold">{selectedEquipment.category}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Serial Number</p>
                      <p className="text-white font-mono font-bold">{selectedEquipment.serial_number}</p>
                    </div>
                    <div className="col-span-2 md:col-span-3">
                      <p className="text-xs text-muted-foreground uppercase">Description</p>
                      <p className="text-white font-bold">{selectedEquipment.description}</p>
                    </div>
                    {selectedEquipment.manufacturer && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Manufacturer</p>
                        <p className="text-white">{selectedEquipment.manufacturer}</p>
                      </div>
                    )}
                    {selectedEquipment.model && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Model</p>
                        <p className="text-white">{selectedEquipment.model}</p>
                      </div>
                    )}
                    {selectedEquipment.measuring_range && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Range</p>
                        <p className="text-white">{selectedEquipment.measuring_range}</p>
                      </div>
                    )}
                    {selectedEquipment.resolution && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Resolution</p>
                        <p className="text-white">{selectedEquipment.resolution}</p>
                      </div>
                    )}
                    {selectedEquipment.tolerance && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Tolerance</p>
                        <p className="text-white">{selectedEquipment.tolerance}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Criticality</p>
                      <p className={`font-bold ${
                        selectedEquipment.criticality === 'Critical' ? 'text-red-400' :
                        selectedEquipment.criticality === 'Reference Only' ? 'text-blue-400' : 'text-white'
                      }`}>{selectedEquipment.criticality}</p>
                    </div>
                  </div>

                  <div className="border border-border rounded-sm p-4 bg-muted/10">
                    <h4 className="text-muted-foreground font-bold uppercase text-sm mb-3">Location & Ownership</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Department</p>
                        <p className="text-white">{selectedEquipment.department || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Location</p>
                        <p className="text-white">{selectedEquipment.storage_location || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Custodian</p>
                        <p className="text-white">{selectedEquipment.custodian || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Responsible</p>
                        <p className="text-white">{selectedEquipment.responsible_person || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-2">
                    {selectedEquipment.status !== 'Scrapped' && (
                      <Button
                        onClick={openEditDialog}
                        variant="outline"
                        className="border-cyan-600 text-cyan-400 hover:bg-cyan-950/50"
                        data-testid="edit-equipment-btn"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Details
                      </Button>
                    )}
                    {selectedEquipment.status !== 'Quarantined' && selectedEquipment.status !== 'Retired' && selectedEquipment.status !== 'Scrapped' && (
                      <>
                        <Button
                          onClick={() => handleStatusChange('Out for Calibration', 'Sent for external calibration')}
                          variant="outline"
                          className="border-purple-600 text-purple-400 hover:bg-purple-950/50"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Send for Calibration
                        </Button>
                        <Button
                          onClick={() => handleStatusChange('Quarantined', 'Manual quarantine')}
                          variant="outline"
                          className="border-red-600 text-red-400 hover:bg-red-950/50"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Quarantine
                        </Button>
                        <Button
                          onClick={() => { setScrapData(prev => ({ ...prev, scrapped_by: user?.email || '' })); setScrapDialogOpen(true); }}
                          variant="outline"
                          className="border-orange-600 text-orange-400 hover:bg-orange-950/50"
                          data-testid="scrap-equipment-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Scrap Item
                        </Button>
                        <Button
                          onClick={() => setRetireDialogOpen(true)}
                          variant="outline"
                          className="border-gray-600 text-gray-400 hover:bg-gray-950/50"
                          data-testid="retire-equipment-btn"
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Retire
                        </Button>
                      </>
                    )}
                    {selectedEquipment.status === 'Out for Calibration' && (
                      <Button
                        onClick={() => {
                          setCalibrationDialogOpen(true);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Record Calibration Return
                      </Button>
                    )}
                    {selectedEquipment.status === 'Quarantined' && (
                      <>
                        <Button
                          onClick={() => handleStatusChange('In Service', 'Released from quarantine after review')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Release from Quarantine
                        </Button>
                        <Button
                          onClick={() => { setScrapData(prev => ({ ...prev, scrapped_by: user?.email || '' })); setScrapDialogOpen(true); }}
                          variant="outline"
                          className="border-orange-600 text-orange-400 hover:bg-orange-950/50"
                          data-testid="scrap-quarantined-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Scrap Item
                        </Button>
                        <Button
                          onClick={() => setRetireDialogOpen(true)}
                          variant="outline"
                          className="border-gray-600 text-gray-400 hover:bg-gray-950/50"
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Retire
                        </Button>
                      </>
                    )}
                    {selectedEquipment.status === 'Scrapped' && (
                      <div className="w-full bg-orange-900/30 border border-orange-600 rounded-sm px-4 py-3">
                        <p className="text-orange-400 text-sm font-bold flex items-center gap-2">
                          <AlertOctagon className="w-4 h-4" />
                          This tooling is SCRAPPED and not available for use
                        </p>
                        {selectedEquipment.scrap_data && (
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-orange-300/80">
                            <div><span className="text-slate-500">Date:</span> {formatDate(selectedEquipment.scrap_data.scrap_date)}</div>
                            <div><span className="text-slate-500">Category:</span> {selectedEquipment.scrap_data.scrap_category}</div>
                            <div className="col-span-2"><span className="text-slate-500">Reason:</span> {selectedEquipment.scrap_data.scrap_reason}</div>
                            <div><span className="text-slate-500">By:</span> {selectedEquipment.scrap_data.scrapped_by}</div>
                            {selectedEquipment.scrap_data.approved_by && <div><span className="text-slate-500">Approved:</span> {selectedEquipment.scrap_data.approved_by}</div>}
                          </div>
                        )}
                      </div>
                    )}
                    {selectedEquipment.status === 'Retired' && (
                      <div className="bg-gray-800/50 border border-gray-600 rounded-sm px-4 py-2">
                        <p className="text-gray-400 text-sm font-bold flex items-center gap-2">
                          <Archive className="w-4 h-4" />
                          This equipment has been retired and is no longer in active service
                        </p>
                        {selectedEquipment.retirement_date && (
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-300/80">
                            <div><span className="text-slate-500">Date:</span> {formatDate(selectedEquipment.retirement_date)}</div>
                            {selectedEquipment.retirement_reason && <div><span className="text-slate-500">Reason:</span> {selectedEquipment.retirement_reason}</div>}
                            {selectedEquipment.retired_by && <div><span className="text-slate-500">By:</span> {selectedEquipment.retired_by}</div>}
                          </div>
                        )}
                      </div>
                    )}
                    {selectedEquipment.status === 'Archived' && (
                      <div className="bg-gray-800/50 border border-gray-500 rounded-sm px-4 py-2">
                        <p className="text-gray-400 text-sm font-bold flex items-center gap-2">
                          <Archive className="w-4 h-4" />
                          This equipment has been archived — historical records preserved
                        </p>
                        {selectedEquipment.archive_date && (
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-300/80">
                            <div><span className="text-slate-500">Date:</span> {formatDate(selectedEquipment.archive_date)}</div>
                            {selectedEquipment.archive_reason && <div><span className="text-slate-500">Reason:</span> {selectedEquipment.archive_reason}</div>}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Delete button — admin only */}
                    {isAdmin() && !['Scrapped'].includes(selectedEquipment.status) && (
                      <Button onClick={openDeleteDialog} variant="outline" className="border-red-800 text-red-400 hover:bg-red-950/50" data-testid="delete-equipment-btn">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Equipment
                      </Button>
                    )}
                  </div>
                </TabsContent>

                {/* Calibration Tab */}
                <TabsContent value="calibration" className="space-y-4 mt-4">
                  <div className="border border-green-800 rounded-sm p-4 bg-green-950/20">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-green-400 font-bold uppercase tracking-wide text-sm">
                        Calibration Status
                      </h4>
                      <Button
                        onClick={() => setCalibrationDialogOpen(true)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="record-calibration-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Record Calibration
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Frequency</p>
                        <p className="text-white font-bold capitalize">{selectedEquipment.calibration_frequency}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Last Calibration</p>
                        <p className="text-white font-bold">
                          {selectedEquipment.last_calibration_date ? formatDate(selectedEquipment.last_calibration_date) : 'Never'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Next Due</p>
                        <p className={`font-bold ${
                          selectedEquipment.is_overdue ? 'text-red-400' :
                          selectedEquipment.is_due_soon ? 'text-amber-400' : 'text-green-400'
                        }`}>
                          {selectedEquipment.next_due_date ? formatDate(selectedEquipment.next_due_date) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Days Remaining</p>
                        <p className={`font-bold ${
                          selectedEquipment.days_until_due < 0 ? 'text-red-400' :
                          selectedEquipment.days_until_due <= 30 ? 'text-amber-400' : 'text-green-400'
                        }`}>
                          {selectedEquipment.days_until_due !== null ? (
                            selectedEquipment.days_until_due < 0 
                              ? `${Math.abs(selectedEquipment.days_until_due)} overdue`
                              : selectedEquipment.days_until_due
                          ) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    {selectedEquipment.current_certificate_number && (
                      <div className="mt-4 pt-4 border-t border-green-800">
                        <p className="text-xs text-muted-foreground uppercase">Current Certificate</p>
                        <p className="text-white font-mono">{selectedEquipment.current_certificate_number}</p>
                      </div>
                    )}
                  </div>

                  {/* Calibration History */}
                  <div className="border border-border rounded-sm">
                    <div className="bg-muted/30 px-4 py-2 border-b border-border">
                      <h4 className="text-sm font-bold text-white uppercase">Calibration History</h4>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {selectedEquipment.calibration_history?.length > 0 ? (
                        selectedEquipment.calibration_history.slice().reverse().map((cal, idx) => (
                          <div key={idx} className="p-4 border-b border-border last:border-b-0">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white font-bold">{formatDate(cal.calibration_date)}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${
                                cal.result === 'Pass' ? 'bg-green-600' : 'bg-red-600'
                              } text-white`}>
                                {cal.result}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground space-y-1">
                              <p>Calibrated by: {cal.calibrated_by}</p>
                              {cal.certificate_number && <p>Certificate: {cal.certificate_number}</p>}
                              {cal.calibration_supplier && <p>Supplier: {cal.calibration_supplier}</p>}
                              {cal.notes && <p>Notes: {cal.notes}</p>}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-muted-foreground">
                          No calibration history
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Intermediate Checks Tab */}
                <TabsContent value="checks" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-white font-bold uppercase">Intermediate Verification Checks</h4>
                    <Button
                      onClick={() => setCheckDialogOpen(true)}
                      variant="outline"
                      className="border-amber-600 text-amber-400 hover:bg-amber-950/50"
                      data-testid="record-check-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Record Check
                    </Button>
                  </div>

                  <div className="border border-border rounded-sm">
                    <div className="max-h-[400px] overflow-y-auto">
                      {selectedEquipment.intermediate_checks?.length > 0 ? (
                        selectedEquipment.intermediate_checks.slice().reverse().map((check, idx) => (
                          <div key={idx} className="p-4 border-b border-border last:border-b-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className="text-white font-bold">{formatDate(check.check_date)}</span>
                                <span className="text-xs text-muted-foreground">{check.check_type}</span>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${
                                check.result === 'Pass' ? 'bg-green-600' : 'bg-red-600'
                              } text-white`}>
                                {check.result}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              <p>Checked by: {check.checked_by}</p>
                              {check.master_reference_used && <p>Reference: {check.master_reference_used}</p>}
                              {check.notes && <p>Notes: {check.notes}</p>}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 text-center text-muted-foreground">
                          No intermediate checks recorded
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* History Tab */}
                <TabsContent value="history" className="space-y-4 mt-4">
                  <div className="border border-border rounded-sm">
                    <div className="bg-muted/30 px-4 py-2 border-b border-border">
                      <h4 className="text-sm font-bold text-white uppercase">Audit Trail</h4>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {selectedEquipment.audit_trail?.length > 0 ? (
                        selectedEquipment.audit_trail.slice().reverse().map((entry, idx) => (
                          <div key={idx} className="p-3 border-b border-border last:border-b-0 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-white font-bold">{entry.action}</span>
                              <span className="text-xs text-muted-foreground">{formatDate(entry.timestamp)}</span>
                            </div>
                            <p className="text-muted-foreground text-xs mt-1">{entry.user}: {entry.details}</p>
                            {entry.previous_status && entry.new_status && entry.previous_status !== entry.new_status && (
                              <p className="text-xs mt-1">
                                <span className="text-muted-foreground">Status: </span>
                                <span className="text-amber-400">{entry.previous_status}</span>
                                <span className="text-muted-foreground"> → </span>
                                <span className="text-green-400">{entry.new_status}</span>
                              </p>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-muted-foreground">
                          No audit history
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Footer */}
              <div className="flex justify-end pt-4 border-t border-border mt-4">
                <Button
                  variant="outline"
                  onClick={() => setDetailDialogOpen(false)}
                  className="border-border"
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Calibration Dialog */}
      <Dialog open={calibrationDialogOpen} onOpenChange={setCalibrationDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="calibration-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Record Calibration - {selectedEquipment?.equipment_id}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleRecordCalibration} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Calibration Date *</Label>
                <Input
                  type="date"
                  value={calibrationData.calibration_date}
                  onChange={(e) => setCalibrationData(prev => ({ ...prev, calibration_date: e.target.value }))}
                  className="bg-muted/50 border-border"
                  data-testid="cal-date-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Calibrated By *</Label>
                <Input
                  value={calibrationData.calibrated_by}
                  onChange={(e) => setCalibrationData(prev => ({ ...prev, calibrated_by: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Person or lab name"
                  data-testid="cal-by-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Method</Label>
                <Select
                  value={calibrationData.calibration_method}
                  onValueChange={(val) => setCalibrationData(prev => ({ ...prev, calibration_method: val }))}
                >
                  <SelectTrigger className="bg-muted/50 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Internal">Internal</SelectItem>
                    <SelectItem value="External">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Result *</Label>
                <Select
                  value={calibrationData.result}
                  onValueChange={(val) => setCalibrationData(prev => ({ ...prev, result: val }))}
                >
                  <SelectTrigger className="bg-muted/50 border-border" data-testid="cal-result-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pass">Pass</SelectItem>
                    <SelectItem value="Fail">Fail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Certificate Number</Label>
                <Input
                  value={calibrationData.certificate_number}
                  onChange={(e) => setCalibrationData(prev => ({ ...prev, certificate_number: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Certificate number"
                  data-testid="cal-cert-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Calibration Supplier</Label>
                <Input
                  value={calibrationData.calibration_supplier}
                  onChange={(e) => setCalibrationData(prev => ({ ...prev, calibration_supplier: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Lab or supplier name"
                />
              </div>
            </div>

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Reference Standard</Label>
              <Input
                value={calibrationData.reference_standard}
                onChange={(e) => setCalibrationData(prev => ({ ...prev, reference_standard: e.target.value }))}
                className="bg-muted/50 border-border"
                placeholder="Traceability reference"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">As Found Condition</Label>
                <Textarea
                  value={calibrationData.as_found_condition}
                  onChange={(e) => setCalibrationData(prev => ({ ...prev, as_found_condition: e.target.value }))}
                  className="bg-muted/50 border-border min-h-[60px]"
                  placeholder="Condition before calibration"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">As Left Condition</Label>
                <Textarea
                  value={calibrationData.as_left_condition}
                  onChange={(e) => setCalibrationData(prev => ({ ...prev, as_left_condition: e.target.value }))}
                  className="bg-muted/50 border-border min-h-[60px]"
                  placeholder="Condition after calibration"
                />
              </div>
            </div>

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Measurement Deviations</Label>
              <Textarea
                value={calibrationData.measurement_deviations}
                onChange={(e) => setCalibrationData(prev => ({ ...prev, measurement_deviations: e.target.value }))}
                className="bg-muted/50 border-border min-h-[60px]"
                placeholder="Any deviations found"
              />
            </div>

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Notes</Label>
              <Textarea
                value={calibrationData.notes}
                onChange={(e) => setCalibrationData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-muted/50 border-border min-h-[60px]"
                placeholder="Additional notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCalibrationDialogOpen(false)}
                className="border-border"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide"
                data-testid="submit-calibration-btn"
              >
                Record Calibration
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Intermediate Check Dialog */}
      <Dialog open={checkDialogOpen} onOpenChange={setCheckDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="check-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-500" />
              Record Intermediate Check - {selectedEquipment?.equipment_id}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleRecordCheck} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Check Date *</Label>
                <Input
                  type="date"
                  value={checkData.check_date}
                  onChange={(e) => setCheckData(prev => ({ ...prev, check_date: e.target.value }))}
                  className="bg-muted/50 border-border"
                  data-testid="check-date-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Checked By *</Label>
                <Input
                  value={checkData.checked_by}
                  onChange={(e) => setCheckData(prev => ({ ...prev, checked_by: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Inspector name"
                  data-testid="check-by-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Check Type *</Label>
                <Select
                  value={checkData.check_type}
                  onValueChange={(val) => setCheckData(prev => ({ ...prev, check_type: val }))}
                >
                  <SelectTrigger className="bg-muted/50 border-border" data-testid="check-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.check_types?.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Result *</Label>
                <Select
                  value={checkData.result}
                  onValueChange={(val) => setCheckData(prev => ({ ...prev, result: val }))}
                >
                  <SelectTrigger className="bg-muted/50 border-border" data-testid="check-result-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pass">Pass</SelectItem>
                    <SelectItem value="Fail">Fail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Master Reference Used</Label>
              <Input
                value={checkData.master_reference_used}
                onChange={(e) => setCheckData(prev => ({ ...prev, master_reference_used: e.target.value }))}
                className="bg-muted/50 border-border"
                placeholder="Reference gauge or standard"
              />
            </div>

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Notes</Label>
              <Textarea
                value={checkData.notes}
                onChange={(e) => setCheckData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-muted/50 border-border min-h-[80px]"
                placeholder="Check observations and notes"
              />
            </div>

            {checkData.result === 'Fail' && (
              <div className="bg-red-950/50 border border-red-600 rounded-sm p-3">
                <div className="flex items-center gap-2 text-red-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-bold">Warning: Failed check will quarantine equipment</span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCheckDialogOpen(false)}
                className="border-border"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className={`font-bold uppercase tracking-wide ${
                  checkData.result === 'Fail' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-amber-600 hover:bg-amber-700'
                } text-white`}
                data-testid="submit-check-btn"
              >
                Record Check
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Retire Equipment Confirmation Dialog */}
      <Dialog open={retireDialogOpen} onOpenChange={setRetireDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="retire-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <Archive className="w-5 h-5 text-gray-500" />
              Retire Equipment
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-amber-950/50 border border-amber-600 rounded-sm p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-bold text-sm">Warning</p>
                  <p className="text-amber-200 text-sm mt-1">
                    Retiring equipment will permanently remove it from active service. 
                    The equipment record will be preserved for audit purposes but cannot be used for calibration or checks.
                  </p>
                </div>
              </div>
            </div>

            {selectedEquipment && (
              <div className="bg-muted/20 border border-border rounded-sm p-4">
                <p className="text-xs text-muted-foreground uppercase mb-1">Equipment to Retire</p>
                <p className="text-white font-bold">{selectedEquipment.equipment_id}</p>
                <p className="text-muted-foreground text-sm">{selectedEquipment.description}</p>
                <p className="text-muted-foreground text-xs mt-1">S/N: {selectedEquipment.serial_number}</p>
              </div>
            )}

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Reason for Retirement</Label>
              <Textarea
                value={retireReason}
                onChange={(e) => setRetireReason(e.target.value)}
                className="bg-muted/50 border-border min-h-[80px] mt-2"
                placeholder="e.g., Equipment damaged beyond repair, Replaced with newer model, No longer required..."
                data-testid="retire-reason-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setRetireDialogOpen(false);
                  setRetireReason('');
                }}
                className="border-border"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRetireEquipment}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold uppercase tracking-wide"
                data-testid="confirm-retire-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                Retire Equipment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Equipment Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-red-500/30 max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Equipment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {deleteCheck && (
              <>
                <div className="bg-muted/20 border border-border rounded-sm p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-slate-500">Equipment:</span> <span className="text-white font-bold">{deleteCheck.equipment_id}</span></div>
                    <div><span className="text-slate-500">Status:</span> <span className="text-white">{deleteCheck.status}</span></div>
                    <div className="col-span-2"><span className="text-slate-500">Description:</span> <span className="text-white">{deleteCheck.description}</span></div>
                    <div><span className="text-slate-500">Serial No:</span> <span className="text-white font-mono">{deleteCheck.serial_number || 'N/A'}</span></div>
                    <div><span className="text-slate-500">Calibration Records:</span> <span className="text-white font-bold">{deleteCheck.calibration_records}</span></div>
                  </div>
                </div>

                {deleteCheck.can_delete ? (
                  <div className="bg-green-950/30 border border-green-600 rounded-sm p-3">
                    <p className="text-green-400 text-sm font-bold">Safe to delete</p>
                    <p className="text-green-300 text-xs mt-1">{deleteCheck.reason}</p>
                  </div>
                ) : (
                  <div className="bg-red-950/30 border border-red-600 rounded-sm p-3">
                    <p className="text-red-400 text-sm font-bold">Cannot permanently delete</p>
                    <p className="text-red-300 text-xs mt-1">{deleteCheck.reason}</p>
                    <p className="text-amber-400 text-xs mt-2 font-bold">Use Archive or Scrap instead to preserve ISO 9001 traceability.</p>
                  </div>
                )}
              </>
            )}

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Deletion Reason (mandatory)</Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="bg-muted/50 border-border min-h-[80px] mt-2"
                placeholder="e.g., Duplicate entry, Entered incorrectly, Scrapped before use..."
                data-testid="delete-reason-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-border">Cancel</Button>
              {deleteCheck?.can_delete ? (
                <Button onClick={handleDeleteEquipment} disabled={!deleteReason.trim() || deleteLoading} className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase" data-testid="confirm-delete-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> {deleteLoading ? 'Deleting...' : 'Permanently Delete'}
                </Button>
              ) : (
                <Button onClick={handleArchiveEquipment} disabled={!deleteReason.trim()} className="bg-gray-600 hover:bg-gray-700 text-white font-bold uppercase" data-testid="archive-instead-btn">
                  <Archive className="w-4 h-4 mr-2" /> Archive Equipment
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Equipment Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-equipment-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-400" />
              Edit Equipment — {selectedEquipment?.equipment_id}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Identification */}
            <div className="border border-cyan-800/50 rounded-lg p-4 bg-cyan-950/20">
              <h4 className="text-cyan-400 font-bold uppercase tracking-wide mb-3 text-sm">Identification</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Type</Label>
                  <Input value={editData.equipment_type || ''} onChange={(e) => setEditData(prev => ({ ...prev, equipment_type: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-type" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Category</Label>
                  <Select value={editData.category || ''} onValueChange={(val) => setEditData(prev => ({ ...prev, category: val }))}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{config?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Description</Label>
                  <Input value={editData.description || ''} onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-description" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Serial Number</Label>
                  <Input value={editData.serial_number || ''} onChange={(e) => setEditData(prev => ({ ...prev, serial_number: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-serial" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Asset Number</Label>
                  <Input value={editData.asset_number || ''} onChange={(e) => setEditData(prev => ({ ...prev, asset_number: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-asset" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Manufacturer</Label>
                  <Input value={editData.manufacturer || ''} onChange={(e) => setEditData(prev => ({ ...prev, manufacturer: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-manufacturer" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Model</Label>
                  <Input value={editData.model || ''} onChange={(e) => setEditData(prev => ({ ...prev, model: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-model" />
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/20">
              <h4 className="text-slate-400 font-bold uppercase tracking-wide mb-3 text-sm">Specifications</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Measuring Range</Label>
                  <Input value={editData.measuring_range || ''} onChange={(e) => setEditData(prev => ({ ...prev, measuring_range: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-range" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Resolution</Label>
                  <Input value={editData.resolution || ''} onChange={(e) => setEditData(prev => ({ ...prev, resolution: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-resolution" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Tolerance</Label>
                  <Input value={editData.tolerance || ''} onChange={(e) => setEditData(prev => ({ ...prev, tolerance: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-tolerance" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Accuracy Class</Label>
                  <Input value={editData.accuracy_class || ''} onChange={(e) => setEditData(prev => ({ ...prev, accuracy_class: e.target.value }))} className="bg-slate-950/50 border-slate-800" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Unit of Measure</Label>
                  <Input value={editData.unit_of_measure || ''} onChange={(e) => setEditData(prev => ({ ...prev, unit_of_measure: e.target.value }))} className="bg-slate-950/50 border-slate-800" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Criticality</Label>
                  <Select value={editData.criticality || 'Standard'} onValueChange={(val) => setEditData(prev => ({ ...prev, criticality: val }))}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {config?.criticality_levels?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Location & Ownership */}
            <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/20">
              <h4 className="text-slate-400 font-bold uppercase tracking-wide mb-3 text-sm">Location & Ownership</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Department</Label>
                  <Select value={editData.department || 'none'} onValueChange={(val) => setEditData(prev => ({ ...prev, department: val === 'none' ? '' : val }))}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-department"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {config?.departments?.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Storage Location</Label>
                  <Input value={editData.storage_location || ''} onChange={(e) => setEditData(prev => ({ ...prev, storage_location: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-location" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Custodian</Label>
                  <Input value={editData.custodian || ''} onChange={(e) => setEditData(prev => ({ ...prev, custodian: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-custodian" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Responsible Person</Label>
                  <Input value={editData.responsible_person || ''} onChange={(e) => setEditData(prev => ({ ...prev, responsible_person: e.target.value }))} className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-responsible" />
                </div>
              </div>
            </div>

            {/* Calibration Setup */}
            <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/20">
              <h4 className="text-slate-400 font-bold uppercase tracking-wide mb-3 text-sm">Calibration Setup</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Calibration Frequency</Label>
                  <Select value={editData.calibration_frequency || 'annually'} onValueChange={(val) => setEditData(prev => ({ ...prev, calibration_frequency: val }))}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-800" data-testid="edit-eq-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {config?.frequencies?.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Calibration Method</Label>
                  <Select value={editData.calibration_method || 'External'} onValueChange={(val) => setEditData(prev => ({ ...prev, calibration_method: val }))}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {config?.calibration_methods?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Calibration Supplier</Label>
                  <Input value={editData.calibration_supplier || ''} onChange={(e) => setEditData(prev => ({ ...prev, calibration_supplier: e.target.value }))} className="bg-slate-950/50 border-slate-800" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Reminder Days</Label>
                  <Input type="number" value={editData.reminder_days || 30} onChange={(e) => setEditData(prev => ({ ...prev, reminder_days: parseInt(e.target.value) || 30 }))} className="bg-slate-950/50 border-slate-800" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Traceability Standard</Label>
                  <Input value={editData.traceability_standard || ''} onChange={(e) => setEditData(prev => ({ ...prev, traceability_standard: e.target.value }))} className="bg-slate-950/50 border-slate-800" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Notes</Label>
              <Textarea value={editData.notes || ''} onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))} className="bg-slate-950/50 border-slate-800 min-h-[60px]" data-testid="edit-eq-notes" />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-700">Cancel</Button>
              <Button onClick={handleSaveEdit} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold uppercase tracking-wide" data-testid="save-edit-btn">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scrap Equipment Dialog */}
      <Dialog open={scrapDialogOpen} onOpenChange={setScrapDialogOpen}>
        <DialogContent className="bg-card border-orange-600/30 max-w-lg" data-testid="scrap-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-orange-500" />
              Scrap Item
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-orange-950/50 border border-orange-600 rounded-sm p-4">
              <div className="flex items-start gap-3">
                <AlertOctagon className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-orange-400 font-bold text-sm">Scrap Confirmation Required</p>
                  <p className="text-orange-200 text-sm mt-1">
                    This will permanently mark the item as scrapped. It will no longer appear in active lists, calibration schedules, or be available for production use. The record is preserved for audit and historical reference.
                  </p>
                </div>
              </div>
            </div>

            {selectedEquipment && (
              <div className="bg-muted/20 border border-border rounded-sm p-4">
                <p className="text-xs text-muted-foreground uppercase mb-1">Item to Scrap</p>
                <p className="text-white font-bold">{selectedEquipment.equipment_id} — {selectedEquipment.description}</p>
                <p className="text-muted-foreground text-xs mt-1">S/N: {selectedEquipment.serial_number} | {selectedEquipment.category} | {selectedEquipment.department || 'No dept'}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Scrap Date *</Label>
                <Input
                  type="date"
                  value={scrapData.scrap_date}
                  onChange={(e) => setScrapData(prev => ({ ...prev, scrap_date: e.target.value }))}
                  className="bg-muted/50 border-border mt-1"
                  data-testid="scrap-date-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Scrap Category *</Label>
                <Select value={scrapData.scrap_category} onValueChange={(val) => setScrapData(prev => ({ ...prev, scrap_category: val }))}>
                  <SelectTrigger className="bg-muted/50 border-border mt-1" data-testid="scrap-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.scrap_categories || []).map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Scrap Reason *</Label>
              <Textarea
                value={scrapData.scrap_reason}
                onChange={(e) => setScrapData(prev => ({ ...prev, scrap_reason: e.target.value }))}
                className="bg-muted/50 border-border min-h-[60px] mt-1"
                placeholder="Describe why this item is being scrapped..."
                data-testid="scrap-reason-input"
              />
            </div>

            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Notes / Comments</Label>
              <Textarea
                value={scrapData.notes}
                onChange={(e) => setScrapData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-muted/50 border-border min-h-[50px] mt-1"
                placeholder="Additional notes..."
                data-testid="scrap-notes-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Scrapped By</Label>
                <Input
                  value={scrapData.scrapped_by}
                  onChange={(e) => setScrapData(prev => ({ ...prev, scrapped_by: e.target.value }))}
                  className="bg-muted/50 border-border mt-1"
                  placeholder={user?.email || 'User'}
                  data-testid="scrap-by-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Approved By</Label>
                <Input
                  value={scrapData.approved_by}
                  onChange={(e) => setScrapData(prev => ({ ...prev, approved_by: e.target.value }))}
                  className="bg-muted/50 border-border mt-1"
                  placeholder="Approver name/email"
                  data-testid="scrap-approved-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setScrapDialogOpen(false);
                  setScrapData({ scrap_date: new Date().toISOString().split('T')[0], scrap_reason: '', scrap_category: '', notes: '', scrapped_by: '', approved_by: '' });
                }}
                className="border-border"
              >
                Cancel
              </Button>
              <Button
                onClick={handleScrapEquipment}
                disabled={!scrapData.scrap_reason || !scrapData.scrap_category || !scrapData.scrap_date}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide disabled:opacity-50"
                data-testid="confirm-scrap-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Confirm Scrap
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalibrationModule;
